/**
 * Phase B3 — the ACCESS token lives in memory only.
 *
 * B1 moved the 7-day *refresh* token into an httpOnly cookie. The access token was left in
 * localStorage so SSE/WS and the module clients kept working; this module removes that last
 * XSS-readable credential. Nothing here is ever written to localStorage or sessionStorage —
 * a successful XSS can still *use* the app as the user, but it can no longer exfiltrate a
 * bearer token and replay it later from somewhere else.
 *
 * The cost of in-memory storage is that a page reload starts with no token. That is fine:
 * the refresh cookie survives the reload, so `bootstrap()` mints a new access token from it.
 * Two consequences drove the rest of this design:
 *
 *  1. **Auth state can no longer be "is there a token?"** — on first paint there never is one.
 *     `client.js` derives auth from the persisted user object plus bootstrap's outcome instead.
 *     (This is the same class of landmine as B1's `if (!localStorage refresh) return null`
 *     gate, which force-logged-out every cookie-only session.)
 *
 *  2. **Refresh tokens rotate, so concurrent refreshes across TABS collide.** The single-flight
 *     in tokenRefresh.js is module-level, i.e. per-tab: two tabs reloaded together would each
 *     present the same cookie and the loser would get a 401 and log the user out — exactly the
 *     bug Workstream I fixed *within* a tab. We serialize across tabs with the Web Locks API;
 *     held sequentially, the second tab simply uses the already-rotated cookie and succeeds.
 */
import axios from 'axios'
import { AUTH_CONFIG, API_CONFIG } from './config'

let accessToken = null

// Who wants to know when the token changes. The one reader today is ModuleSwitchContext: which held
// modules a person is shown depends on their COMPANY, which it learns by asking the server with this
// token — and on first paint there is no token yet (see the header), so it has to hear when one
// arrives, and when the session ends.
const listeners = new Set()

function notify() {
  for (const listener of [...listeners]) {
    try {
      listener(accessToken)
    } catch {
      // A listener's failure is its own; it must never break signing in or out.
    }
  }
}

/**
 * Call `listener(token)` after every set or clear — including a clear that leaves no token where there
 * was none, because that is how a failed bootstrap says "there is no session" in this tab. Returns the
 * unsubscribe function.
 */
export function onAccessTokenChange(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// WHY THERE IS NO TOKEN, when there is none. A refresh the server REFUSED (401/403) proves the session
// is over. A refresh that met a 429 (identity allows 120 requests a minute per address, and a whole
// office can share one), a 5xx (identity restarting in a deploy), a timeout or a dropped connection
// proves nothing - yet the request after it goes out with no token, takes a 401, and the shared
// client's 401 branch used to read that as "session expired" and send the person to /login for the
// crime of being busy. This flag is the difference. It is set only by an inconclusive refresh and
// cleared by any token, so the idle path (which sends a token-less request ON PURPOSE) is untouched.
let refreshInconclusive = false

/** Did the last attempt to mint a token fail for a reason that says nothing about the session? */
export function refreshWasInconclusive() {
  return refreshInconclusive
}

/** Record how the last refresh ended: `true` = no verdict (429, 5xx, network), `false` = a verdict. */
export function markRefreshInconclusive(value) {
  refreshInconclusive = !!value
}

/** A refusal that proves the session is over, as opposed to one that proves nothing about it. */
export function refusalEndsSession(err) {
  const status = err?.response?.status
  return status === 401 || status === 403
}

/**
 * THE ONE TEST every sign-out path asks before acting on a 401: is this 401 only the echo of a busy
 * minute? True when there is no token, the refresh that should have minted one met no verdict, and a
 * signed-in person is still stored. Then the 401 is the server answering an unauthenticated request
 * this browser could not help sending - not the session ending - and nothing may be cleared over it.
 *
 * One function for all of them (shared client, the older identity client, useAuth's two checks) so
 * they cannot drift: the first version of this fix put the rule in one client and the review found
 * the older client, three screens away, still signing people out on the same 401.
 */
export function sessionKeptThroughBusyMinute() {
  return !accessToken && refreshInconclusive && !!localStorage.getItem(AUTH_CONFIG.USER_KEY)
}

export function getAccessToken() {
  return accessToken
}

export function setAccessToken(token) {
  accessToken = token || null
  if (accessToken) refreshInconclusive = false
  notify()
}

export function clearAccessToken() {
  accessToken = null
  notify()
}

/**
 * Run `fn` under a cross-tab exclusive lock when the browser supports Web Locks.
 * Falls back to running directly — degraded (the multi-tab race returns) but never broken,
 * which matters because Web Locks is unavailable in non-secure contexts.
 */
export async function withRefreshLock(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    return navigator.locks.request('bfs_token_refresh', fn)
  }
  return fn()
}

let bootstrapPromise = null

/**
 * Mint an access token from the httpOnly refresh cookie. Called once at app start, and again
 * by tokenRefresh when it finds no in-memory token.
 *
 * Resolves to the token, or null when there is genuinely no valid session (the normal case for
 * a logged-out visitor — a 401 here is expected, not an error worth surfacing).
 */
export function bootstrap() {
  if (bootstrapPromise) return bootstrapPromise

  bootstrapPromise = withRefreshLock(async () => {
    try {
      const resp = await axios.post(
        `${API_CONFIG.IDENTITY.baseURL}/auth/refresh`,
        null,
        { withCredentials: true, headers: { 'X-Requested-With': 'XMLHttpRequest' } },
      )
      const token = resp.data?.access_token || null
      setAccessToken(token)
      if (resp.data?.user) {
        localStorage.setItem(AUTH_CONFIG.USER_KEY, JSON.stringify(resp.data.user))
      }
      return token
    } catch (err) {
      // Only a definitive rejection from the server proves there is no session. Dropping the cached
      // user removes USER_KEY, which fires a storage event that navigates EVERY OTHER TAB to /login
      // (useSessionTimeout's cross-tab handler) — so treating a network blip, timeout, 429 or 5xx as
      // "logged out" used to end the user's session in every tab, including one with unsaved work.
      // On a transient failure we return null (this attempt has no token) but leave the session
      // intact; bootstrapPromise is cleared in `finally`, so the next call simply retries.
      if (refusalEndsSession(err)) {
        refreshInconclusive = false
        clearAccessToken()
        localStorage.removeItem(AUTH_CONFIG.USER_KEY)
      } else {
        refreshInconclusive = true
      }
      return null
    } finally {
      bootstrapPromise = null
    }
  })

  return bootstrapPromise
}
